import { useState, useEffect, useCallback } from 'react';
import userService from 'features/administrativo/services/userService';

export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar lista de usuários
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const usersData = await userService.getUsers();
      setUsers(usersData);
    } catch (err) {
      setError(err.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  // Criar usuário
  const createUser = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const newUser = await userService.createUser(userData);
      setUsers(prev => [...prev, newUser]);
      return newUser;
    } catch (err) {
      setError(err.message || 'Erro ao criar usuário');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar usuário
  const updateUser = async (id, userData) => {
    setLoading(true);
    setError(null);
    try {
      const updatedUser = await userService.updateUser(id, userData);
      setUsers(prev => prev.map(user => 
        user.id === id ? updatedUser : user
      ));
      return updatedUser;
    } catch (err) {
      setError(err.message || 'Erro ao atualizar usuário');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Excluir usuário
  const deleteUser = async (id) => {
    setLoading(true);
    setError(null);
    try {
      await userService.deleteUser(id);
      setUsers(prev => prev.filter(user => user.id !== id));
    } catch (err) {
      setError(err.message || 'Erro ao excluir usuário');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Carregar usuários na inicialização
  useEffect(() => {
    loadUsers();
  }, []);

  // Recarregar usuários quando o contexto de empresa mudar
  useEffect(() => {
    const handleCompanyContextChanged = () => {
      loadUsers();
    };

    window.addEventListener('company-context-changed', handleCompanyContextChanged);
    return () => {
      window.removeEventListener('company-context-changed', handleCompanyContextChanged);
    };
  }, []);

  return {
    users,
    loading,
    error,
    loadUsers,
    createUser,
    updateUser,
    deleteUser
  };
};

export const useUserPermissions = (userId) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar permissões do usuário
  const loadPermissions = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getUserPermissions(userId);
      
      // Extrair permissões da resposta
      let permissionsData = [];
      if (response && response.success && response.data) {
        // Se a resposta tem estrutura { success: true, data: {...} }
        if (Array.isArray(response.data)) {
          permissionsData = response.data;
        } else {
          // Se data é um objeto com categorias, extrair todas as permissões
          permissionsData = Object.values(response.data).flat();
        }
      } else if (Array.isArray(response)) {
        // Se a resposta é diretamente um array
        permissionsData = response;
      }
      
      setPermissions(permissionsData);
    } catch (err) {
      console.error('Erro ao carregar permissões:', err);
      setError(err.message || 'Erro ao carregar permissões');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Sincronizar permissões
  const syncPermissions = async (permissionNames) => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const result = await userService.syncUserPermissions(userId, permissionNames);
      await loadPermissions(); // Recarregar permissões
      return result;
    } catch (err) {
      setError(err.message || 'Erro ao sincronizar permissões');
      throw err;
    } finally {
      setLoading(false);
    }
  };


  // Carregar permissões quando userId muda
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  return {
    permissions,
    loading,
    error,
    loadPermissions,
    syncPermissions
  };
};
